import { useGet } from '@/query/fetchHooks';
import { useParams } from 'react-router-dom';
import { useState, useContext, useEffect } from 'react';
import { Button } from '@teo/components';
import {
  Shapes,
  TimeFilled,
  TaskCheckmarkFilledNew,
} from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { convertToTime } from '@/util';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { sortDataLP } from '@/components/LearningPath/util';
import { NavContext } from '@/components/NavContext';
import { LearnContent } from '@/components/LearningPath/Learn/LearnContent';
import { LpProgress } from '@/components/LearningPath/Learn/LpProgress';
import ConnectSectionPreview from './ConnectSectionPreview';

function LearningPathPreview() {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const { lpgroupId, learningpathId } = useParams();
  const [lpGroup, setLpGroup] = useState<any>(null);
  const [nodes, setNodes] = useState<any>(null);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [allNodes, setAllNodes] = useState<any>(null);
  const [selectNode, setSelectNode] = useState(null);

  const { setLeftSidebar, setHeaderDisabled } = useContext(NavContext);
  const [allCategories, setAllCategories] = useState([]);
  const [includeCertificate, setIncludeCertificate] = useState(null);

  useEffect(() => {
    setLeftSidebar(true);
    return () => setLeftSidebar(false);
  }, []);

  useEffect(() => {
    setHeaderDisabled(true);
    return () => setHeaderDisabled(false);
  }, []);

  useGet(`/myusedlps`, {
    organisationId: auth?.user?.activeOrganisationId,
    userId: auth?.user?.id,
    learningpathId: lpgroupId || learningpathId,
    onSuccess: (res: any) => {
      setLpGroup(res?.data[0] || undefined);
    },
  });

  useGet(`/learningpathnodedetails`, {
    learningpathId: lpgroupId || learningpathId,
    userId: auth?.user?.id,
    onSuccess: async (res: any) => {
      const nodedetails = res?.data?.filter(
        (el: any) =>
          el?.type !== 'root' &&
          (el?.learningpathId == lpgroupId ||
            el?.learningpathId == learningpathId)
      );
      if (nodedetails?.length > 0) {
        const sortData = sortDataLP(nodedetails);
        setNodes(sortData);

        setActiveItem(sortData[0]);

        const mlsCertificate = sortData?.find(
          (el) => el?.subType === 'milestone' && el?.hasCertificate
        );
        setIncludeCertificate(mlsCertificate);
      }
    },
  });

  useGet(`/learningpathnodedetailsflat`, {
    topLearningpathId: lpgroupId || learningpathId,
    onSuccess: async (res: any) => {
      const lpAllNodes = res?.data?.filter((nds: any) => nds?.type !== 'root');
      setAllNodes(lpAllNodes);
    },
  });

  useGet(`/categories`, {
    sort: 'tag',
    organisationId: `${auth?.user?.activeOrganisationId},OR,organisationId=null`,
    onSuccess: (result: any) => {
      setAllCategories(
        result?.data?.map((el: any) => ({
          id: el.id,
          label: el.tag,
          icon: el.icon,
        })) || []
      );
    },
  });

  if (!activeItem) return null;

  return (
    <div className="-mx-4 flex flex-col">
      <div className="relative -mt-4 h-[640px] w-full overflow-hidden bg-grey-01">
        {nodes?.length > 0 && (
          <ConnectSectionPreview
            lpgroupId={lpgroupId || learningpathId}
            items={nodes}
            activeItem={activeItem}
            allCategories={allCategories}
            setSelectNode={setSelectNode}
            selectNode={selectNode}
            auth={auth}
            angleDeg={30}
            layoutRandomness={{ distanceFactorRange: [0.6, 1.5] }}
          />
        )}
      </div>
      <div className="my-8 mx-auto flex w-full max-w-[720px] flex-col px-0 sm:px-4">
        <div className="flex w-full flex-col border-b px-4 sm:px-0">
          <p className="text-sm text-grey-05">{t('pages.lp_landing.lp')}</p>
          <h3 className="mt-1 text-xl">{lpGroup?.lpName}</h3>
          {/* <div className="my-4 flex items-center">
            {activeItem && allNodes && (
              <LpProgress
                allNodes={nodes}
                lpGroup={lpGroup}
                activeItem={activeItem}
              />
            )}
          </div> */}
          {lpGroup?.lpDescription && (
            <p className="text-grey-08">{lpGroup?.lpDescription}</p>
          )}

          <div className="mt-4 flex gap-4">
            {allNodes?.length > 0 && (
              <div className="flex items-center">
                <Shapes className="h-4 w-4 text-grey-04" />
                <p className="pl-1 text-sm text-grey-05">
                  {allNodes?.length} {t('pages.lp_landing.items')}
                </p>
              </div>
            )}

            {/*lpGroup?.lpEstimatedTime > 0 && (
              <div className="flex items-center">
                <TimeFilled className="h-4 w-4 text-grey-04" />
                <p className="pl-1 text-sm text-grey-05">
                  {convertToTime(
                    Math.abs(lpGroup?.lpEstimatedTime),
                    t('pages.learn_index.hour'),
                    t('pages.learn_index.minute')
                  )}
                </p>
              </div>
            )*/}

            {includeCertificate && (
              <div className="flex items-center">
                <TaskCheckmarkFilledNew className="h-4 w-4 text-grey-04" />
                <p className="pl-1 text-sm text-grey-05">
                  {t('pages.landing_training.certificate')}
                </p>
              </div>
            )}
          </div>
          <div className="my-8">
            <Button disabled={true} onClick={() => {}}>
              {t('pages.lp_landing.btn_resume')}
            </Button>
          </div>
        </div>
        {allNodes?.length > 0 && (
          <div className="mt-8 px-4 sm:px-0">
            <h3 className="mb-4 text-base">{t('pages.lp_landing.this_lp')}</h3>
            <LearnContent allNodes={allNodes} />
          </div>
        )}
      </div>
    </div>
  );
}

export default LearningPathPreview;
